import { T, useGT } from 'gt-react';
import { useWizard } from '@erp/ui/hooks/use-wizard';
import {
  Wizard,
  WizardFooter,
  WizardHeader,
  WizardPanel,
  WizardRail,
  WizardSurface,
} from 'src/components/ui/wizard';
import { Button } from 'src/components/ui/button';
import {
  MappingPanel,
  SchemaPanel,
  SourcePanel,
  TablePanel,
} from './DataMappingWizardPanels';
import { useDataMappingWizardState } from './useDataMappingWizardState';
import { buildDataMappingWizardSteps } from './buildDataMappingWizardSteps';
import { analyzeKeyUniqueness } from './keyUniqueness';

/**
 * Create-a-data-mapping flow:
 *
 *   1. Source   — upload a file, reuse an existing data asset, or skip entirely
 *   2. Schema   — columns, inferred from the source when there is one
 *   3. Table    — a new stateful table from those columns, or an existing one
 *   4. Mapping  — name it, then create (gate)
 *
 * Steps 3 and 4 are gates: creating a stateful table provisions a real
 * Postgres table, and creating the mapping is what business logic then reads.
 *
 * Everything but the layout lives elsewhere — state and side effects in
 * `useDataMappingWizardState`, the step list in `buildDataMappingWizardSteps`,
 * step bodies in `DataMappingWizardPanels`, the create sequence in
 * `createDataMappingFlow`, and the step branching in `wizardStepChecks`.
 */
export function DataMappingWizard({
  onCreated,
  onCancel,
}: {
  onCreated: (dataMappingId: string) => void;
  onCancel: () => void;
}) {
  const gt = useGT();
  const state = useDataMappingWizardState(onCreated);
  const wizard = useWizard({
    onComplete: state.handleComplete,
    steps: buildDataMappingWizardSteps(state, gt),
  });

  return (
    <Wizard api={wizard} className="h-[640px]">
      <WizardRail />
      <WizardSurface>
        <WizardHeader />
        <div className="flex-1 overflow-y-auto px-5 py-5">
          <WizardPanel stepId="source">
            <SourcePanel
              sourceMode={state.sourceMode}
              onSourceModeChange={state.setSourceMode}
              file={state.file}
              onFileChange={state.handleFileChange}
              assetName={state.assetName}
              onAssetNameChange={state.setAssetName}
              existingAssetId={state.existingAssetId}
              onExistingAssetIdChange={state.setExistingAssetId}
              readyAssets={state.readyAssets}
              source={state.source}
              uploading={state.uploading}
              uploadError={state.uploadError}
            />
          </WizardPanel>

          <WizardPanel stepId="schema">
            <SchemaPanel
              tableMode={state.tableMode}
              columns={state.columns}
              keyColumnIds={state.keyColumnIds}
              columnErrors={state.columnErrors}
              canInfer={!!state.source}
              suggesting={state.suggesting}
              suggestError={state.suggestError}
              suggestNotes={state.suggestNotes}
              onInfer={state.handleSuggestColumns}
              onAddColumn={state.addColumn}
              onChangeColumn={state.changeColumn}
              onRemoveColumn={state.removeColumn}
              onMakeKey={state.toggleKeyColumn}
              keyCandidates={state.keyCandidates}
              onApplyKeyCandidate={state.applyKeyCandidate}
              keyAnalysis={analyzeKeyUniqueness(
                state.sample,
                state.keyColumnNames,
              )}
            />
          </WizardPanel>

          <WizardPanel stepId="table">
            <TablePanel
              tableMode={state.tableMode}
              onTableModeChange={state.setTableMode}
              tableName={state.tableName}
              onTableNameChange={state.setTableName}
              tableDescription={state.tableDescription}
              onTableDescriptionChange={state.setTableDescription}
              existingTableId={state.existingTableId}
              onExistingTableIdChange={state.setExistingTableId}
              tables={state.tables}
              columnCount={state.columns.length}
              keyColumnNames={state.keyColumnNames}
              mappingName={state.mappingName}
            />
          </WizardPanel>

          <WizardPanel stepId="mapping">
            <MappingPanel
              mappingName={state.mappingName}
              onMappingNameChange={state.handleMappingNameChange}
              mappingSlug={state.mappingSlug}
              onMappingSlugChange={state.handleMappingSlugChange}
              mappingDescription={state.mappingDescription}
              onMappingDescriptionChange={state.setMappingDescription}
              hasSource={!!state.source}
              syncNow={state.syncNow}
              onSyncNowChange={state.setSyncNow}
              submitError={state.submitError}
              onSuggestName={state.handleSuggestMappingName}
              canSuggestName={state.canSuggestMappingName}
              namingSuggestion={state.namingSuggestion}
              nameSuggestError={state.nameSuggestError}
            />
          </WizardPanel>
        </div>

        <WizardFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            disabled={state.busy}
          >
            <T context="button to abandon the data mapping wizard">Cancel</T>
          </Button>
        </WizardFooter>
      </WizardSurface>
    </Wizard>
  );
}
